import { STREAM_PATH_CAMERA_CONFIG } from "lib/consts/streamPathOverlay";
import { computePinholeIntrinsics } from "lib/navigation/pinholeIntrinsics";
import { yawFromRosOrientation } from "lib/protocols/mapFilterYaml";
import { Point2D, Pose } from "types/geometry";

export type StreamPathCameraConfig = {
    heightM: number;
    pitchRad: number;
    hfovDeg: number;
    vfovDeg: number;
    forwardOffsetM: number;
    minForwardM: number;
    maxForwardM: number;
};

export type VideoPixelPoint = Point2D;

/** Map-frame point → robot base_link (x forward, y left). */
export function mapPointToRobotBase(
    point: Point2D,
    robotX: number,
    robotY: number,
    robotYaw: number,
): Point2D {
    const dx = point.x - robotX;
    const dy = point.y - robotY;
    const cos = Math.cos(robotYaw);
    const sin = Math.sin(robotYaw);
    return {
        x: cos * dx + sin * dy,
        y: -sin * dx + cos * dy,
    };
}

/** Ground point in robot base_link → native video pixel, or null if not visible. */
export function projectGroundPointToVideoPixel(
    forwardM: number,
    leftM: number,
    videoWidth: number,
    videoHeight: number,
    config: StreamPathCameraConfig = STREAM_PATH_CAMERA_CONFIG,
): VideoPixelPoint | null {
    if (
        forwardM < config.minForwardM ||
        forwardM > config.maxForwardM ||
        !videoWidth ||
        !videoHeight
    ) {
        return null;
    }

    const px = forwardM - config.forwardOffsetM;
    const py = leftM;
    const pz = -config.heightM;

    const cosA = Math.cos(config.pitchRad);
    const sinA = Math.sin(config.pitchRad);

    const zc0 = px;
    const xc0 = -py;
    const yc0 = -pz;

    const xc = xc0;
    const yc = cosA * yc0 + sinA * zc0;
    const zc = -sinA * yc0 + cosA * zc0;

    if (zc <= 0.05) {
        return null;
    }

    const { fx, fy, cx, cy } = computePinholeIntrinsics(
        videoWidth,
        videoHeight,
        config.hfovDeg,
        config.vfovDeg,
    );

    const u = fx * (xc / zc) + cx;
    const v = fy * (yc / zc) + cy;

    if (!Number.isFinite(u) || !Number.isFinite(v)) {
        return null;
    }

    return { x: u, y: v };
}

/** Project Nav2 planned path (map frame) into native video pixels. */
export function projectPlannedPathToVideoPixels(
    path: Point2D[],
    robotPose: Pose,
    videoWidth: number,
    videoHeight: number,
    config: StreamPathCameraConfig = STREAM_PATH_CAMERA_CONFIG,
): (VideoPixelPoint | null)[] {
    if (!path.length || !robotPose?.position || !robotPose?.orientation) {
        return [];
    }

    const yaw = yawFromRosOrientation(robotPose.orientation);
    const robotX = robotPose.position.x;
    const robotY = robotPose.position.y;

    return path.map((point) => {
        const robotBase = mapPointToRobotBase(point, robotX, robotY, yaw);
        return projectGroundPointToVideoPixel(
            robotBase.x,
            robotBase.y,
            videoWidth,
            videoHeight,
            config,
        );
    });
}
